/**
 * Metric-tag cardinality allowlist (ADR-069). Pure logic, no vendor SDK, so it
 * is shared by the `datadog-node` provider (the last-line guard, so a
 * high-cardinality tag can't reach a metric even by accident) and re-exported
 * by `@galapagos/telemetry` for the builder layer.
 */

import { ENUM_ATTRIBUTE_VALUES } from './safe-attributes';

/** Bounded dimensions permitted as metric tags. */
export const SAFE_METRIC_TAGS = new Set<string>([
  // Unified conversation list: why the clinical lane was withheld, and a
  // bounded size axis for the ordering cost. Both are declared dimensions of
  // metrics this package would otherwise strip silently, leaving the series
  // present but undifferentiated.
  'clinical_scope',
  'conversation_count_bucket',
  'service',
  'env',
  'version',
  'route',
  'method',
  'status_class',
  'result',
  // `field` is bounded by SAFE_FIELD_VALUES below: it names which column of a
  // row failed a parse, never the row's value.
  'field',
  // Member detail change publish-failure breakdown (closed enums, ADR-069).
  'request_source',
  'flow_version_present',
  // `type` is the closed ADR-052 domain-event enum (bounded) — a legitimate
  // breakdown dimension for the event_publish_* counters. `status`/`skill`/
  // `event`/`ticket` on the harness emit() metrics are likewise bounded (one
  // ticket per feature) — kept in sync with the emit() tag set so the allowlist
  // and the emitter agree (the harness runs on noop, so this is latent parity).
  'type',
  'status',
  'skill',
  'event',
  'ticket',
  // Enrollment domain-signal dimensions — all bounded: `program_domain` is the
  // closed program-domain enum (substance/behavioral/mood), `is_main` is a
  // boolean, `reason` is a small closed set of failure codes (enrollment
  // outcomes, plus the provider SDK exception names on the telemedicine
  // provider-delete counter, plus the slack-notifier's closed skip-reason set
  // on `galapagos.slack_notifier.skipped` — missing_token / missing_channel /
  // internal_member / already_delivered / root_receipt_missing /
  // escalation_missing / renderer skips — plus the Tier 1 migration importer's
  // closed row-loss set on `galapagos.member_migration.tier1.rows_dropped` —
  // no_start_instant / specialist_unresolved / specialist_slot_taken — plus the
  // check-in promise's closed withheld-reason set on
  // `galapagos.reward.promise.withheld` — not_enrolled / lookup_failed / zero_points /
  // cap_reached / already_earned_today — plus memory lifecycle archive reasons
  // speculative_expired / stale — plus the onboarding rewards-intro gate's
  // closed answer set on `galapagos.onboarding.rewards_intro_gate.decided` —
  // eligible / ineligible / pending / failed — plus the staff gate's closed
  // verify-failure set on `galapagos.internal_identity.failure{stage:verify}` —
  // missing_token / invalid_token / missing_claims — all bounded by construction).
  'program_domain',
  'is_main',
  'reason',
  // Consent document rendering — `consent_type` is the closed
  // MEMBER_CONSENT_TYPE vocabulary (eight values), the declared breakdown of
  // the member_documents.rendered counter; never a member identifier.
  'consent_type',
  // Registration consent capture — which capture recorded the consent, the
  // closed `signup | migration` pair on `member_consents.recorded`.
  'consent_flow',
  // Reminder write path — the closed four-value set (create / replace / archive /
  // write_by_definition) on the `reminders.mutation_error` counter. Bounded by
  // construction; carries no member id and no reminder content (ADR-069).
  'operation',
  // Reward earn behaviour — the closed `operation_subtype` enum
  // (first_daily_check_in / attended_appointment / …), the only breakdown
  // dimension on the `galapagos.reward.earn` counter. Bounded by construction; carries no
  // member id and no substance-revealing dimension (ADR-069).
  'operation_subtype',
  // Migration import path — closed `tier0` | `tier1` on consent and resolution
  // counters. Value-guarded below (same closed tier0|tier1 set as parental import metrics).
  'path',
  // Redemption product discriminator — the closed `product_type` enum
  // (`e_gift` | `gift_item`), the only breakdown dimension on the
  // `galapagos.reward.redemption` counter. Bounded; no member id, no substance dimension
  // (rewards is condition-agnostic, ADR-015/ADR-069).
  'product_type',
  // Home-card decision dimensions — all bounded: `visible` is a boolean
  // rendered as a string, `unread_bucket` is the closed none/1/2-9/10+ set on
  // the care-message decision counter, and `arm` is the BAM experiment's
  // closed variant set (no-bam/day-0/day-1). `arm` and `visible` also unblock
  // the pre-existing bam_home_card.decision breakdown, whose tags were being
  // dropped by this allowlist despite being bounded by construction.
  'visible',
  'unread_bucket',
  'arm',
  // Home care-section resolution — closed state ids and a boolean fallback flag.
  'care_section_state',
  'care_section_fallback',
  // Voice-session dimensions — all bounded closed enums: `voice_id` is the
  // code-defined voice catalog (a handful of curated voices), `source` is the
  // resolution tier (`preference` | `metadata` | `default`) or the analytics
  // forwarder's `domain-event` origin, and `agent_definition_id` is the
  // server-owned agent-definition catalog (a small fixed set). Safe as metric
  // breakdowns per ADR-069's bounded-cardinality rule.
  'voice_id',
  'source',
  'token_source',
  'agent_definition_id',
  // Journey outcome dimensions (the done-marking metric) — all closed enums
  // from the shared journey vocabulary: `source_tier` (authored | generative |
  // fallback for the done-marking metric; real | simulated | dummy for the
  // migration readiness metric) and `generation_reason` (nightly | retrigger |
  // manual). Bounded by construction, no member data.
  'source_tier',
  'generation_reason',
  // Which welcome series a member's first composed day came from — a closed
  // two-value enum from code (v1 | v2), value-guarded below.
  'welcome_series',
  // The journey planner's safety action on a slot (`galapagos.journey.composer.
  // planner_safety`): the closed journey veto class it acted on, whether it
  // redirected or omitted the slot, and which run shape it happened in. All
  // three are code vocabularies, value-guarded below; no planner text rides.
  'veto_class',
  'planner_action',
  'journey_run_mode',
  // Voice-agent purpose (`intake` / `practice` / …) — one value per agent
  // definition in the prompts catalogue, bounded by construction.
  'agent_type',
  // Escalation source (`assessment_suicide_screen_positive` |
  // `agent_crisis_flag` | `transcript_crisis_scan` |
  // `intake_withdrawal_screen_positive`) — the closed ESCALATION_SOURCE
  // vocabulary, four values by construction. Deliberately
  // NOT the `source` key above: that one is value-constrained to the
  // voice-resolution tiers plus the forwarder origin, so an escalation value
  // passed under it is dropped at the emit boundary without an error — the
  // same class of trap as a redaction-key collision.
  'escalation_source',
  // The claim route that lost a race on `galapagos.escalation.claim_conflict`
  // (`claim`, `release` or `take_over`): how often two people reach the same
  // escalation, by action. Value-constrained below, so nothing but the route
  // name can ride it.
  'escalation_claim_action',
  // How a clinician resolved an escalation on `galapagos.escalation.handled` —
  // the source of the false-positive rate. Value-constrained below to the
  // closed disposition vocabulary, so no free-text resolution detail can ride it.
  'escalation_disposition',
  // Memory-extraction breakdown dimensions (ADR-075) — all closed enums from
  // the frozen taxonomy: `category` (16 memory categories), `confidence_tier`
  // (4 tiers), `level` (dedup `hash`|`levenshtein`).
  'category',
  'confidence_tier',
  'level',
  // Post-call pipeline dimensions — bounded closed sets: `outcome` is a
  // per-metric result enum (the member-binding
  // bound | no_hint | no_session | identity_mismatch, the projection
  // projected | duplicate, the conversation-ingestion written | error),
  // `event_type` is the provider's webhook event kind
  // (post_call_transcription | post_call_audio), and `conversation_type` is the
  // agent definition that produced the conversation — exactly one value per
  // CURRENTLY-REGISTERED definition, plus a single `unknown` fallback covering
  // both an unidentifiable conversation and a handle no longer in the registry.
  // The emitter validates against the live registry before tagging precisely so
  // this holds: the ingestion document carries a retired handle verbatim, but
  // the tag must not inherit every value ever written to the notNull-text
  // `voice_sessions.agent_definition_id` column, which would bound cardinality
  // by history instead of by the registry.
  // The app-update-policy read adds `fresh | cache | timeout | error`, a
  // closed set owned by the policy controller.
  // The post-call crisis scan adds `raised | duplicate | suppressed_in_call`,
  // the closed result of deciding whether a stored verdict escalates.
  // The programme-episode end adds `ended | already_ended | not_active |
  // not_found | invalid`, the closed result of one end call.
  // The staff member-access decision adds `assigned | recorded_justification |
  // justification_required | member_not_found` on
  // `galapagos.internal_members.access_decision`.
  // `galapagos.personalisation.dash_normalised` adds `repaired | replaced`,
  // what the engine did about banned dashes in the copy it returned.
  // The rewards eligibility read adds `enrolled | eligible | flag_off |
  // no_matched_client | no_config | not_cm_eligible | legacy_unenrolled |
  // legacy_rewards_unsettled | lookup_failed` on
  // `galapagos.reward.cm_eligibility.checked`.
  // All are low-cardinality breakdown dimensions with no identity content.
  'outcome',
  // Healthie appointment reconciliation sweep — member pages processed (numeric
  // string, low cardinality bounded by APPOINTMENT_SWEEP_MAX_MEMBERS).
  'pages_processed',
  // Which identifier space a staff member reference resolved in, on
  // `galapagos.internal_members.access_decision`: the closed
  // `member_id | legacy_backend2_sub | none`. The KIND of identifier, never the
  // identifier itself.
  'id_space',
  // Rx driving-licence read path — closed backend2 | store | healthie | none.
  'licence_source',
  // Which system ended a programme episode: the closed PROGRAM_END_SOURCE_KIND
  // pair (`healthie_pact` | `atlas_staff`). Never the form answer or staff id.
  'source_kind',
  // `event_type` carries two closed vocabularies: the ElevenLabs webhook set
  // documented above, and the staff-notification consumer's four handled bus
  // types (`care.chat.message.sent`, `care.team.{assigned,promoted,unassigned}`),
  // bounded by the same constant its EventBridge rule pattern is built from.
  'event_type',
  'conversation_type',
  // Screen/entry TTI dimensions are closed vocabularies: `screen_id` comes
  // from the shared ScreenId registry (value-enforced against the mirror
  // below), while entry and launch classes are small code-owned enums. None
  // can contain member data.
  'screen_id',
  'entry_point',
  'launch_class',
  'step_key',
  // Where a settle-gated navigation routed, as a closed code-owned enum of
  // route names (e.g. left-off | meet-sona). Never a member value.
  'destination',
  // Notification-launch dimensions — both closed code-owned enums. `stage` is
  // the three-step resolution ladder (row_lookup | delivery_state |
  // content_parse) that says WHERE a degrade happened, and pairs with the
  // already-allowlisted `reason` that says what; charting one without the
  // other collapses the ladder. `launch_kind` is the client's three-value
  // entry class (cold | warm | unknown). Neither can carry member data.
  'stage',
  'launch_kind',
  // Where a tapped notification landed on the client's open counter: a mirror
  // of NOTIFICATION_OPEN_LANDING_OUTCOME (`@galapagos/shared`, which this
  // package cannot import), value-enforced below and pinned by the open-record
  // test in apps/mobile. Never an intent id.
  'landing_outcome',
  // Review-commit readiness splits only the two closed source plan kinds.
  'source_plan_kind',
  // Orderable product family (closed, value-enforced below): splits staff
  // order counts by kind without ever naming a sku, a member or a program.
  'product_kind',
  // Which copy the Sona state notice showed (closed, value-enforced below):
  // a new member or one re-onboarding. Never the member's state.
  'notice_variant',
  // A Sona launch refused for an AI-restricted member (closed, value-enforced
  // below): what was asked for and where the refusal happened. Never the
  // reason the member is restricted.
  'sona_intent_kind',
  'sona_launch_entry',
  // How a Sona route's wait on the consent standing ended (closed,
  // value-enforced below): admitted, sent Home as restricted, closed by the
  // member, or left before it settled.
  'sona_route_hold_outcome',
  'sona_admission_outcome',
  // Member document kind on the chart-filing outcome (closed, value-enforced
  // below): splits chat transcript and attachment filings from consent and
  // signed-form ones. Domain-qualified, never the bare `kind`.
  'document_kind',
  // Pharmacy order pipeline (closed, value-enforced below): which vendor, what
  // the order came to, and which vendor call. None can carry a member, a drug
  // or an rx number — they are the routing outcome and the call step only.
  'pharmacy_vendor',
  'pharmacy_order_outcome',
  'pharmacy_vendor_step',
  // The actionable-backlog gauge (closed, value-enforced below): which status
  // an order is waiting in, and a cumulative age bucket. Counts per bucket,
  // never an order.
  'pharmacy_order_status',
  'pharmacy_order_age',
  // Where a quit date write came from (closed, value-enforced below): the app
  // or the care team. Its own key rather than `source`, whose value set
  // belongs to another vocabulary.
  'quit_date_source',
  // Which source served a member's medication read (closed, value-enforced
  // below). Its own key rather than `source`, whose value set belongs to
  // another vocabulary.
  'medication_source',
  // Module progress counters: the catalogue module key and where the
  // completion came from (both closed, value-enforced below). Its own source
  // key, like `quit_date_source`, because `source` belongs to another set.
  'module_key',
  'module_completion_source',
  // The module's status just before a staff reset, on
  // `galapagos.module_progress.reset` (closed, value-enforced below). Never the
  // reset reason or the staff member.
  'prior_module_status',
  // Voice transcript timestamps either come directly from the provider's
  // bounded start field or from the bounded event-time fallback.
  'voice_timestamp_origin',
  // `reduce_motion` is a stringified boolean — trivially bounded at two values.
  // It is the breakdown a to-interactive histogram on a choreographed screen
  // exists for: the reduced-motion path skips the whole sequence, so pooling the
  // two together would report a bimodal distribution as one meaningless average.
  'reduce_motion',
  // Device push platform — the closed ios/android pair (PUSH_PLATFORM),
  // bounded by the member_devices CHECK constraint. The app-update-policy
  // metrics also tag it with React Native's `Platform.OS`, the same pair.
  'platform',
  // `permission_changed` is a stringified boolean on the push-permission report
  // counter: whether the stored status differed from the reported one. Two
  // values, so bounded; it is the split between launch re-reports and real flips.
  'permission_changed',
  // Telemedicine call-push dispatch leg — the closed voip/alert pair naming
  // which transport a per-device attempt took. Bounded by construction.
  'leg',
  // Notification-pipeline dimensions — both bounded closed sets: `channel` is
  // the delivery-transport enum (push/email, NOTIFICATION_CHANNEL) and
  // `definitionKey` is the code-hosted notification catalog key (a handful of
  // entries, bounded by construction). Safe as metric breakdowns (ADR-069).
  'channel',
  'definitionKey',
  // The member-facing notification category (`check_ins`, `sona_follow_ups`,
  // …) — a key from the server-owned category catalog, a handful of values by
  // construction, never a member value.
  'category_key',
  // Terminal reason on the notification dispatch counters — the closed
  // DISPATCH_TERMINAL_REASONS vocabulary in @galapagos/shared, value-enforced
  // against the mirror below. Domain-qualified rather than the bare `reason`
  // key above deliberately: that one is value-constrained in practice to the
  // enrollment and telemedicine failure codes, so a dispatch reason passed
  // under it would make both breakdowns silently ambiguous — the bare-noun
  // trap this file warns about for `source`, `role`, and `escalation_source`.
  'dispatch_reason',
  // Server-side telemedicine appointment completion path on
  // `galapagos.telemedicine.appointment_completed` — mirror of
  // `CareAppointmentCompletionPath` in `apps/api/src/care-scheduling/types.ts`.
  'telemedicine_completion_path',
  // Notification device-row classification on prune metrics — closed enum
  // (`expo`, `expo_with_voip`, `voip_only`, `malformed`), never a token.
  'row_kind',
  // Which OTP throttle gate refused an initiate: a three-value closed set
  // (min_interval / destination_hour / ip_hour) fixed in code. It is the whole
  // point of the `auth.otp.initiate.throttled` counter — without it the three
  // refusals collapse into one series and a slow-inbox retry storm is
  // indistinguishable from an attack on a single destination (ADR-069 Rule 4).
  // Also which way the onboarding rewards-intro gate sent a member, show / skip,
  // on `galapagos.onboarding.rewards_intro_gate.decided`.
  'gate',
  // Eligibility-cascade dimensions — all bounded by the closed
  // ELIGIBILITY_PROVIDER_NAMES vocabulary: `provider` is the resolving tier on
  // the resolved metric, and `from_provider`/`to_provider` are the escalation
  // step's adjacent tiers on the escalated metric. Provider names are a
  // code-defined enum, so cardinality is fixed by construction (ADR-069). The
  // names stay domain-qualified deliberately: a bare `from`/`to` here would
  // permanently whitelist those generic names for every future metric.
  'provider',
  'from_provider',
  'to_provider',
  // SAML SSO registration dimensions — all closed, code-owned sets: the
  // partner (`amazon | cigna | unknown`, from the code-owned partner map), the
  // eligibility match input mode (`demographic | external_id`), and the OTP
  // contact the member chose (`email | phone`). Qualified rather than bare
  // `partner`/`mode`/`contact` so those generic names stay off the allowlist
  // for every future metric (ADR-069).
  'saml_partner',
  'match_mode',
  'saml_contact',
  // Form context resolution — the closed set of form context provider keys in
  // the form-context registry, so cardinality is fixed by construction
  // (ADR-069). Qualified rather than the bare `provider` above, which belongs to
  // the eligibility cascade's vocabulary.
  'context_provider',
  'context_key',
  // Companies-provider request endpoint — the closed two-value enum
  // (`companies-directory` / `company-config-lookup`) that appears as a
  // breakdown on both galapagos.company_directory.internaltools.request
  // and galapagos.company_config.internaltools.request. Domain-qualified
  // name (not a bare `endpoint`) to satisfy the file's own bare-noun rule.
  // Bounded by construction (ADR-069).
  'companies_endpoint',
  // Program-availability derivation path — the closed six-value enum
  // (happy_intersection / happy_grants_fallback / happy_intersection_empty /
  // sad_config_only / sad_empty_no_match / sad_config_error) tag on the
  // galapagos.program_availability.derived metric. Domain-qualified
  // (not a bare `path`) to satisfy the file's own bare-noun rule.
  // Bounded by construction (ADR-069).
  'program_availability_path',
  // Registration-completion prefill mix — the closed roster-coverage vocabulary
  // (`full` / `partial` / `none`), bounded by construction (ADR-069).
  'prefill',
  // Billing trigger dimensions — all bounded: `config` is the closed
  // billing-trigger config enum (standard/conservative), `tier` is the closed
  // care-pathway tier enum, and `zero_baseline_applied` and `appended` (whether
  // an anchor skip wrote a tier-series row) are stringified booleans — two
  // values each. Safe as metric breakdowns per ADR-069.
  'config',
  'tier',
  'zero_baseline_applied',
  'appended',
  // Migration replay: which step owner declined or accepted a replay request.
  // The closed owner tuple of the shared step manifest (six values), never
  // member-derived. It separates the consumers that share one tier, without
  // which every non-runner `wrong_tier` skip reads as the tier runner's own.
  'runner',
  // Decision-producer dimensions, both bounded closed sets. `member_binding` is
  // attributed | unattributed | malformed_event — how the run related to a
  // member, never WHICH member. `dry_run` is a stringified boolean.
  //
  // These are not optional breakdowns: the unattributed and malformed-event
  // skips carry `member_binding` as their ONLY tag, so without it here they
  // filter down to no tags at all and collapse into one indistinguishable
  // bucket — which is precisely the "receiving garbage" vs "sitting idle"
  // distinction the counter exists to draw.
  'member_binding',
  'dry_run',
  // Custom-tag collision dimensions, both bounded closed sets.
  // `custom_tag_resolution` is the three-value MEMBER_CUSTOM_TAG_RESOLUTION enum
  // (`created` | `builtin` | `existing`); `custom_tag_op` is `create` | `rename`.
  //
  // Like `member_binding` above, these are not optional breakdowns — they are
  // the entire content of the signal. The counter exists to answer "is the
  // built-in catalog missing vocabulary members keep reaching for?", which is
  // exactly the `builtin`-vs-`existing` split; filtered out, every collision
  // collapses into one undifferentiated total that answers nothing.
  //
  // BOTH are domain-qualified rather than the bare `resolution` / `op` they read
  // as at the emit site, and for the same reason: this allowlist is keyed by tag
  // NAME globally, so blessing a bare noun grants that key to every future
  // metric, along with whatever unbounded value it chooses to put there.
  // Qualifying keeps the grant scoped to this counter's closed value sets.
  'custom_tag_resolution',
  'custom_tag_op',
  // Conversation-turn speaker on per-turn distributions — the closed
  // `user | agent` pair, bounded by the transcript projection's role filter.
  'role',
  // Voice get_check_ins query span — integer 1–31 (bounded by
  // MAX_CHECK_IN_QUERY_DAYS) serialized as a string. Stratifies the
  // `galapagos.voice_tools.get_check_ins` counter by requested range width;
  // bounded by construction (Zod validation rejects > 31 days upstream).
  'date_range_days',
  // Voice search_help result size — integer 0–3 serialized as a string,
  // bounded by the search's fixed result limit. Never the query itself.
  'result_count',
  // Voice-tools guard label — build_practice | get_check_ins | search_help |
  // unknown. `unknown` deliberately covers a newly-added protected route until
  // it receives a dedicated breakdown, so its denial counter cannot evade the
  // surface-wide monitor.
  'tool',
  // Conversation-realtime breakdowns are all closed protocol vocabularies.
  // The transport deliberately has no conversation/message/participant tag.
  'authorization_operation',
  'event_kind',
  'failure_class',
  // Change Healthcare patient signals: `ch_patient` is the closed CH patient
  // kind (v1 | v2 | none) and `ch_action` the closed CH XML action
  // (hsilabel_search | person_put_partial | person_search_hsi | security_logout
  // | organization_get | rx_search_org | rx_search_patient_history).
  // Namespaced because the bare `action` must stay denied. Neither carries a
  // member, a person number or anything CH returned.
  'ch_patient',
  'ch_action',
  // Prescription-sync dimensions. `facility_id` is the bounded, config-defined
  // set of Change Healthcare facility ids (about 50) — an operational site
  // code, not PHI, and never a patient or person identifier. The rest are
  // closed vocabularies fixed by the sync code: `rx_sync_source` (sweep |
  // backfill | reconcile), `rx_change` (created | updated | cancelled |
  // unchanged), `rx_attribution` (owned | not_owned | unknown |
  // unattributable) and `rx_sync_job` (sweep | reconcile | backfill).
  // Prefixed so they never admit a bare `source`/`change` on unrelated
  // counters; no drug name, rx number or hsi value ever rides a metric.
  'facility_id',
  'rx_sync_source',
  'rx_change',
  'rx_attribution',
  'rx_sync_job',
  // The CH prescription date a parse failure names, held to the closed
  // ch_date_field vocabulary below; never the date text itself.
  'ch_date_field',
  'finalization_transition',
  // Person-sync entry point on the person_sync_failed counter — the closed
  // `signed_up | migrated | chat_imported | eligibility_resolved | capture_unanchored |
  // sweep | window_closed | ai_availability_changed | ai_availability` set (the
  // consumer's invocation modes).
  // Bounded by construction, no member data.
  'trigger',
  // The catalogue outcome id of a re-sent analytics outcome on
  // `galapagos.analytics.pre_anchor_replayed` (`member.account.created`, …).
  // A closed catalogue vocabulary, value-checked against the outcome-id
  // grammar below so a free-form string can never ride the key.
  'outcome_id',
  // Healthie DSM-5 diagnoses ladder — the closed MEMBER_DIAGNOSIS_KIND set on
  // `galapagos.healthie_diagnoses.kind` aggregate counters only; never beside a
  // member id (ADR-069).
  'diagnosis_kind',
  // Healthie reconcile — which pull stream a run or item belongs to, the closed
  // HEALTHIE_RECONCILE_STREAMS pair on `galapagos.healthie.reconcile.*`.
  'healthie_reconcile_stream',
  // Default-pool pick tier on `galapagos.care_assignment.default_assignment`
  // — the closed DefaultPoolSelection set (`rules_matched | unrestricted |
  // fallback`). Present only on `assigned` outcomes; never a programme key or
  // specialist id (ADR-069).
  'selection',
  // Medical-information order read — closed `origin` breakdown on
  // `galapagos.rx.medical_information.order_read` (native | legacy_mirror |
  // healthie_live | none). Domain-qualified name would be preferable, but the
  // spec's declared attribute is `origin` and the value set is closed.
  'origin',
  // Automated care-outreach dimensions — all closed sets by construction, and
  // spelled exactly as the emitters write them (domain-qualified snake_case,
  // never the camelCase row fields they derive from — the notifications
  // pipeline's `definitionKey` above is a different catalog): `definition_key`
  // is the code-hosted outreach catalog (one entry today), `content_source` is
  // `personalised | template | human`, `authorship` is the three-value
  // MESSAGE_AUTHORSHIP enum, `guard_target` is `message | push`,
  // `review_action` is the closed OUTREACH_REVIEW_ACTION set, `context_ports`
  // is the count of present context ports (0–10) and `push_enqueued` is a
  // stringified boolean. The other keys the outreach emitters use — `outcome`,
  // `reason`, `failure_class`, `dry_run`, `type`, `provider` — are the shared
  // entries above. Rendered copy, member names and zones never ride a metric —
  // they are row-only (ADR-069).
  'definition_key',
  'content_source',
  'authorship',
  'guard_target',
  'review_action',
  'context_ports',
  'push_enqueued',
  // Personalisation content-guard dimensions on
  // `galapagos.personalisation.guard` — each a closed set checked by value
  // below: `content_kind` is the registered content-kind key, `content_format`
  // the output format, `guard_candidate` which candidate was screened and
  // `guard_rule` the rule a push failed (`none` on a pass). Domain-qualified,
  // never the bare `kind` / `format` / `rule`. The screened copy never rides a
  // metric (ADR-069).
  'content_kind',
  'content_format',
  'guard_candidate',
  'guard_rule',
  // AWS account-limit slug on the quota-usage gauge — the closed reader set
  // (iam_roles / rds_clusters / event_buses / cfn_stacks /
  // cloudfront_distributions). Domain-qualified (not a bare `quota`) per this
  // file's own bare-noun rule; the monitor's `by {aws_quota}` multi-alert
  // grouping depends on this tag surviving the filter.
  'aws_quota',
  // Care-chat realtime unavailability cause — the closed two-value enum
  // (`config_absent` | `subscription_error`) on the
  // galapagos.care_chat.realtime_unavailable counter. Domain-qualified rather
  // than a bare `cause` to satisfy the file's bare-noun rule; bounded by
  // construction (ADR-069).
  'care_chat_realtime_cause',
  // Which audience asked the analytics-context endpoint for its identity — the
  // closed `member | staff | unknown` enum (`unknown` when no token verified).
  // Bounded by construction (ADR-069).
  'actor_type',
  // The booking that wrote a care-team assignment, grouped to the closed
  // `welcome | treatment_plan | provider | follow_up` set. Bounded by
  // construction (ADR-069).
  'call_type_group',
  // The routable care call type (closed `CARE_CALL_TYPE` vocabulary) and which
  // staff slot read ran — `pool` or `specialist`. Both bounded by construction
  // (ADR-069).
  'call_type',
  'read_scope',
  // Call length in minutes: an option of a call type (15/30/45/60 for a
  // follow-up, one length otherwise), or on a rejected request the length the
  // client asked for, clamped by the emitter so a hostile value cannot mint
  // unbounded tag values (ADR-069).
  'duration_minutes',
  'requested_minutes',
  // Which product surface a signal came from — `mobile`, `atlas`, `web`, a
  // care-team presentation read's `home_card | profile_care_team`, or the
  // rewards eligibility read's `onboarding`. Each emitter passes a literal from
  // its own closed set; never a person (ADR-069).
  'surface',
  // Member-record access audit counter dimensions. `access_basis` is the closed
  // four-value MEMBER_ACCESS_BASES vocabulary (bounded by the table's CHECK);
  // `access_audit_mode` is the closed `observe | enforce` failure-mode switch,
  // qualified rather than a bare `mode` for the reason given for
  // `custom_tag_op` above. Neither names a person or a member (ADR-069).
  'access_basis',
  'access_audit_mode',
  // Staff care-chat denials and read-only reads. `estate` is where the denied
  // request's member came from (`migrated | native | unknown`, bounded by
  // SAFE_ESTATE_VALUES); `basis` is what admitted a read-only viewer
  // (`assigned | recorded_justification`, bounded by SAFE_BASIS_VALUES).
  // Neither names a person or a member (ADR-069).
  'estate',
  'basis',
  // Staff org role on the member-scope counter — the closed 9-value
  // INTERNAL_ROLE vocabulary, bounded by the internal_users role CHECK.
  // Deliberately NOT the bare `role` key above: that one is value-constrained
  // in practice to the conversation-turn `user | agent` pair, so putting an org
  // role under it would make that breakdown silently ambiguous — the same
  // bare-noun trap this file warns about for `source` and `escalation_source`.
  // Descriptive only: it is a telemetry dimension and never an authorization
  // input, which ADR-087 Rule 1 requires and a gate-side guard test asserts.
  'staff_role',
  // Adhoc PSTN call admission footing — the closed four-value
  // CARE_ADHOC_CALL_ACCESS_BASES set, bounded by the care_adhoc_calls CHECK.
  // Qualified rather than reusing `access_basis` above, whose values are the
  // member-record access audit's and differ; never a person (ADR-069).
  'call_access_basis',
  // Staff capability-tier denial breakdown — the two dimensions of the
  // galapagos.staff_authz.tier_denied counter, without which the counter answers
  // only "how many" and not either question it exists for.
  //
  //   `required_group` is a sorted join over the closed INTERNAL_GROUP catalogue,
  //   so its cardinality is bounded by that tuple and changes only when the
  //   catalogue does. It separates a read-tier denial from a write-tier one,
  //   which is what tells an operator whether a flip stopped people reading
  //   member records or only writing to them.
  //
  //   `staff_path` is the two-value `internal | legacy` issuer enum. A denial on
  //   the legacy path means an unpopulated crosswalk; the same denial on the
  //   internal path means a missing group. Same counter, opposite remedy.
  //
  // Both descriptive only — telemetry dimensions, never an authorization input.
  'required_group',
  'staff_path',
  // Team-granted route admission — closed enum team_key plus route
  // template; never a caller or member id (ADR-069).
  'team_key',
  // Access-grant audit-read breakdown — the two dimensions of the
  // galapagos.access_grants.audit_read* counters. Both bounded by construction,
  // and both named rather than inferred:
  //
  //   `unfiltered` is a boolean rendered as a string — the one dimension that
  //   separates "somebody queried the access log" from "somebody asked for all
  //   of it", which is the signal worth alerting on.
  //
  //   `filters` is the SORTED, comma-joined list of filter AXIS NAMES supplied
  //   (`from,internal_user_id,member_id,to`), never their values. Four optional
  //   axes give 15 non-empty combinations plus the unfiltered case; the sort is
  //   what makes that a bound rather than a permutation count, since without it
  //   one filter shape would have several spellings. No member id, no staff id,
  //   and no instant reaches the tag — those are log attributes, not
  //   dimensions.
  'unfiltered',
  'filters',
  // Access-grant WRITE breakdown — the one dimension of the
  // galapagos.access_grants.recorded counter: which reason a care person gave
  // for reaching a member they are not assigned to. Bounded by the closed
  // `P2_ACCESS_JUSTIFICATION_REASON` vocabulary (nine values), and validated at
  // the request schema and again by the table's CHECK before anything is
  // emitted, so no unbounded value can reach the tag even from a malformed
  // caller.
  //
  // The accessor's id is deliberately NOT here. It is the point of the log
  // ATTRIBUTE beside this counter — a privileged act and who performed it — but
  // a staff uuid is unbounded cardinality, which Rule 4 denies as a dimension.
  // Neither is the reason's free text or its display label: those are the
  // justification itself, and the metric records that one was given, not what
  // it said.
  'reason_code',
  // Member-list scope dimensions. `empty` is a boolean rendered as a string;
  // `scope` is the closed selector the read was served under — an unnarrowed
  // caseload, one the caller filtered, or an identity that could not be resolved
  // to an internal user at all. Three values, all enumerable at compile time.
  //
  // `scope` and `empty` are both bare words, which this file warns about
  // elsewhere, and the warning applies to each: a second surface with a
  // different notion of either would make the breakdown ambiguous exactly as
  // `source` did. They are kept because both are properties of a READ with
  // closed value sets, so the values stay enumerable — but a future emitter must
  // qualify its own key (`chart_scope`, `roster_empty`) rather than reuse these.
  // `empty` is the weaker of the pair: it carries less domain meaning, so it is
  // the likelier to be reached for by something unrelated.
  //
  // Neither carries a member id, an internal user id or a search term; the
  // point of the counter is to show that the scope is in force and how often a
  // caseload is empty, which needs no identifier (ADR-069).
  'scope',
  'email_match',
  'empty',
  // The emergency-mirror blocks' own scope, qualified rather than folded into
  // `scope` above. Both emitters live in the internal-members service, so a
  // shared key would make "group by scope" mix block names (`contact`,
  // `clinic`) with caseload narrowing (`caseload`, `caseload_filtered`,
  // `unresolved`) — the ambiguity the note on `scope` predicts and the one
  // `source` already caused. Two values, enumerable at compile time, and it
  // names no member.
  'emergency_scope',
  // Care-team attribution-trail dimension — the closed four-value action enum
  // (assigned / unassigned / promoted / demoted), the only breakdown on the
  // assignment_audit_written counter. Bounded by construction and carries
  // neither the member nor either staff identity (ADR-069).
  //
  // Qualified rather than a bare `action`: this list is global, and an
  // unqualified key would also admit the tag on unrelated counters that
  // already pass one and have it dropped here — changing their emitted
  // dimensions as a side effect of this slice.
  'care_team_action',
  // Where a migrated member's reward enrolment snapshot came from — a dedicated
  // key with its own closed value set below, never folded into the shared
  // `source` grab-bag. Six values fixed by the importer's own branches; it
  // carries no member, identity or catalog value.
  'snapshot_source',
  // Staff contact-card write dimensions. Both bounded, and both qualified by
  // their domain rather than taken as a bare `kind` / `fields`: this set is
  // keyed by tag NAME globally, so a generic name makes one meaning serve
  // every later producer that reaches for the same word. `address_kind` is the
  // closed home|shipping enum; `contact_field_count` is 1 or 2, because there
  // are two channels and a save that changed neither is refused before it can
  // emit. Neither carries an address, an email or a phone number — the edited
  // value has no key it could arrive under.
  'address_kind',
  'contact_field_count',
  // Whether a care-team check-in could name its acting staff member — a closed
  // two-value set below. The flag only; the staff id itself is never a tag.
  'author_identity',
  // Reporting cohort on member-level counters — the closed internal / member /
  // unknown set below, derived from the internal-member classifier so testers
  // can be filtered out of a dashboard. The classification only; the email it
  // is computed from never reaches a tag.
  'cohort',
  // Whether the member came from the 1.0 migration — the closed migrated /
  // new / unknown set below, read from the migration marker. The flag only;
  // never the member id it is read for.
  'member_origin',
  // Migration exec gauges: a migrated member's check-in engagement before the
  // 1.0 freeze, and how check-in days after the migration compare with the
  // same-length window before it. All three closed sets are value-guarded
  // below, so a count never carries anything but a bucket name.
  'prior_activity',
  'window',
  'direction',
  // Members by AI availability: whether Sona may be offered and, when it is
  // restricted, why. Both closed sets are value-guarded below. Dedicated keys
  // rather than the shared open `status` / `reason`, so the values can be
  // closed; a member's state code is never a value.
  'ai_availability_status',
  'ai_restricted_reason',
  // Which content-guard screen rejected a producer's generated copy — a closed
  // four-value set below. Qualified rather than a bare `rule`: other counters
  // already pass a `rule` tag that is dropped here, and allowlisting the bare
  // key would change their emitted dimensions as a side effect.
  'copy_guard_rule',
]);

/** High-cardinality identifiers denied as metric dimensions (fine on logs/spans). */
export const DENIED_METRIC_TAG_KEYS = new Set<string>([
  'identity_id',
  'usr.id',
  'user_id',
  'member_id',
  'session_id',
  'request_id',
  'trace_id',
  'span_id',
]);

/** An interpolated route (carries an id segment) must not be a metric tag. */
function looksInterpolated(value: string): boolean {
  return /\/\d+(\/|$)/.test(value) || /[0-9a-f]{8}-[0-9a-f]{4}/i.test(value);
}

/** Closed value set for the `source` tag: voice-resolution tiers + forwarder origin. */
// `client_report` / `transcript` are the journey outcome decision paths — a
// two-value closed set from the shared OUTCOME_SOURCES vocabulary.
const SAFE_SOURCE_VALUES = new Set([
  // The care-team presentation served: a named primary, the unassigned
  // state, or the identity-free fallback.
  'derived',
  'unassigned',
  'fallback',
  'domain-event',
  'preference',
  'metadata',
  'default',
  'client_report',
  'transcript',
  // The push-delivery handler that owns a push-control session, so a circuit
  // opening can be traced to the sweep or event consumer it happened in.
  'sweeper',
  'care_chat_push',
  'call_push',
  // Registration acuity for next-call derivation (stored row vs live fallback).
  'stored',
  'live',
  // Acuity assessment write path (intake, Tier 0 import, backfill CLI).
  'registration',
  'legacy_import',
  'backfill',
]);
// Token-resolution origin on the sendoso token-TTL gauge — a dedicated key
// (NOT the shared, grab-bag `source`), two-value closed set: served from the
// in-memory cache vs a fresh secret read.
// The analytics catalogue's outcome-id grammar (dot-separated kebab
// segments), or the `unknown` a replay reports for a row it could not rebuild.
const SAFE_OUTCOME_ID_VALUE = /^(?:unknown|[a-z][a-z0-9-]{0,40}(?:\.[a-z0-9-]{1,40}){1,4})$/;
const SAFE_TOKEN_SOURCE_VALUES = new Set(['cache', 'fetch']);
const SAFE_CONSENT_FLOW_VALUES = new Set(['signup', 'migration']);
const SAFE_VOICE_TOOL_VALUES = new Set([
  'build_practice',
  'get_check_ins',
  'search_help',
  'unknown',
]);
const SAFE_VOICE_TIMESTAMP_ORIGIN_VALUES = new Set(['provider_start', 'derived_from_event']);
const SAFE_MEDICAL_ORDER_READ_ORIGIN_VALUES = new Set([
  'native',
  'legacy_mirror',
  'healthie_live',
  'none',
]);
const SAFE_DIAGNOSIS_KIND_VALUES = new Set([
  'aud',
  'oud',
  'cud',
  'stud_amphetamine',
  'stud_cocaine',
]);
const SAFE_HEALTHIE_RECONCILE_STREAM_VALUES = new Set(['forms', 'signatures']);
const SAFE_SOURCE_PLAN_KIND_VALUES = new Set(['foundation', 'weekly']);
export const SAFE_LANDING_OUTCOME_VALUES = new Set([
  'routed',
  'not_presented',
  'gated',
  'rejected',
  'dropped',
  'no_destination',
]);
/**
 * Why a staff caller saw no clinical conversations. The three values are
 * mutually exclusive and are the whole point of the tag: `no_tier` is a
 * configuration fault that follows the caller everywhere, `no_participation`
 * is the ordinary per-thread case that fires constantly, and collapsing them
 * would bury the fault inside a counter that ticks on every page view.
 */
const SAFE_CLINICAL_SCOPE_VALUES = new Set(['granted', 'no_tier', 'no_participation']);
/** Conversation-count buckets: a bounded size axis, never a raw count. */
const SAFE_CONVERSATION_COUNT_BUCKET_VALUES = new Set(['0-5', '6-20', '21-50', '50+']);
const SAFE_WELCOME_SERIES_VALUES = new Set(['v1', 'v2']);
// Mirrors the shared JOURNEY_VETO_CLASSES vocabulary (this package does not
// depend on @galapagos/shared).
const SAFE_VETO_CLASS_VALUES = new Set([
  'crisis',
  'self_harm',
  'withdrawal',
  'medical',
  'check_failed',
]);
const SAFE_PLANNER_ACTION_VALUES = new Set(['redirect', 'omit']);
const SAFE_JOURNEY_RUN_MODE_VALUES = new Set(['ordinary', 'first_day']);
const SAFE_NOTICE_VARIANT_VALUES = new Set(['onboarding', 'migration']);
const SAFE_SONA_INTENT_KIND_VALUES = new Set(['chat', 'practice']);
const SAFE_SONA_LAUNCH_ENTRY_VALUES = new Set(['launch', 'route', 'refusal']);
const SAFE_SONA_ROUTE_HOLD_OUTCOME_VALUES = new Set([
  'admitted',
  'restricted',
  'dismissed',
  'abandoned',
]);
const SAFE_SONA_ADMISSION_OUTCOME_VALUES = new Set([
  'admitted',
  'restricted',
  'consent_required',
  'app_setup_owed',
  'error',
  'abandoned',
]);
const SAFE_ESCALATION_CLAIM_ACTION_VALUES = new Set([
  'claim',
  'release',
  'take_over',
  'manager_release',
  'assign',
]);

/**
 * Closed value set for the `escalation_disposition` tag — the same vocabulary
 * the log attribute of that name is held to, so there is one copy to keep in
 * step with ESCALATION_DISPOSITION.
 */
export const SAFE_ESCALATION_DISPOSITION_VALUES = new Set<string>(
  ENUM_ATTRIBUTE_VALUES.escalation_disposition,
);
const SAFE_CH_DATE_FIELD_VALUES = new Set<string>(ENUM_ATTRIBUTE_VALUES.ch_date_field);
const SAFE_IMPORTED_RESOLUTION_PATH_VALUES = new Set(['tier0', 'tier1']);

// Enrolment-snapshot provenance on the member-migration importer: the mapped
// 1.0 snapshot landed, the live gate had already enrolled the member, the
// source carried no catalog assignment, it carried only ids nothing maps, the
// column could not be read at all, the member was un-enrolled upstream and no
// row was written, or their 1.0 eligibility was not verified and no row was
// written.
const SAFE_SNAPSHOT_SOURCE_VALUES = new Set([
  'enrolee',
  'existing',
  'empty',
  'dropped',
  'unreadable',
  'skipped_ineligible',
  'skipped_unverified',
]);

/**
 * Closed value set for the `product_kind` tag — a mirror of
 * `ORDER_PRODUCT_KIND` (`packages/shared/src/orders/catalogue.ts`). The key is
 * only bounded because its values come from that const. This package sits below
 * `@galapagos/shared` in the dependency graph, so the const cannot be imported;
 * the metric-tags test drift-guards this mirror against the catalogue source.
 */
export const SAFE_PRODUCT_KIND_VALUES = new Set<string>(['nrt', 'test_kit']);

/**
 * Closed value set for the `document_kind` tag — a mirror of
 * `MEMBER_DOCUMENT_KIND` (`packages/shared/src/members/consents.ts`). This
 * package sits below `@galapagos/shared` in the dependency graph, so the const
 * cannot be imported; the metric-tags test drift-guards this mirror against the
 * source.
 */
export const SAFE_DOCUMENT_KIND_VALUES = new Set<string>([
  'consent',
  'signed_form',
  'drivers_licence',
  'chat_transcript',
  'chat_attachment',
]);

/**
 * Closed value sets for the three pharmacy-order tags — mirrors of
 * `PHARMACY_VENDOR`, `PHARMACY_ORDER_OUTCOME` (whose tail is
 * `PHARMACY_ORDER_STATUS`) and `PHARMACY_VENDOR_STEP` in
 * `packages/shared/src/rx/pharmacy-orders.ts`. This package sits below
 * `@galapagos/shared`, so the consts cannot be imported; the metric-tags test
 * drift-guards each mirror against that source.
 */
export const SAFE_PHARMACY_VENDOR_VALUES = new Set<string>(['gogomeds', 'healthwarehouse']);
/** The member estate a staff care-chat denial is tagged with. */
export const SAFE_ESTATE_VALUES = new Set<string>(['migrated', 'native', 'unknown']);
/** What admitted a read-only staff care-chat viewer. */
export const SAFE_BASIS_VALUES = new Set<string>(['assigned', 'recorded_justification']);
export const SAFE_PHARMACY_ORDER_OUTCOME_VALUES = new Set<string>([
  'not_ours',
  'not_owned',
  'pre_ownership',
  'cancelled_at_creation',
  'unconfigured',
  'pending',
  'submitted',
  'held',
  'rejected',
  'failed',
  'cancelled',
  'handled',
]);
export const SAFE_PHARMACY_VENDOR_STEP_VALUES = new Set<string>([
  'token',
  'patient',
  'customer',
  'order',
  'submit',
]);

/**
 * Closed value sets for the actionable-backlog gauge's tags — mirrors of
 * `PHARMACY_ORDER_ACTIONABLE_STATUS` and `PHARMACY_ORDER_BACKLOG_AGE` in the
 * same shared file, drift-guarded by the same test.
 */
export const SAFE_PHARMACY_ORDER_STATUS_VALUES = new Set<string>(['held', 'rejected', 'failed']);
export const SAFE_PHARMACY_ORDER_AGE_VALUES = new Set<string>([
  'any',
  'over_1h',
  'over_4h',
  'over_24h',
]);

/**
 * Closed value set for the `quit_date_source` tag — a mirror of
 * `MEMBER_QUIT_DATE_SOURCE` (`packages/shared/src/members/quit-dates.ts`). This
 * package sits below `@galapagos/shared` in the dependency graph, so the const
 * cannot be imported here; the quit-date write telemetry test in `apps/api`,
 * which sees both, asserts the two are equal.
 */
export const SAFE_QUIT_DATE_SOURCE_VALUES = new Set<string>([
  'mobile_app',
  'atlas_staff',
  'system',
  'backend2_import',
]);

/**
 * Closed value set for the `medication_source` tag: live Change Healthcare
 * data, the one-time 1.0 snapshot, or no prescription at all. The member
 * medication telemetry test in `apps/api` asserts its own source vocabulary
 * stays inside this set.
 */
export const SAFE_MEDICATION_SOURCE_VALUES = new Set<string>([
  'change_healthcare',
  'legacy_snapshot',
  'none',
]);

/**
 * Closed value sets for the module progress tags — mirrors of `MODULE_KEYS`
 * and `MODULE_COMPLETION_SOURCE` (`packages/shared/src/modules/module-catalogue.ts`).
 * This package sits below `@galapagos/shared`, so the consts cannot be imported
 * here; the module progress telemetry test in `apps/api`, which sees both,
 * asserts they are equal.
 */
export const SAFE_MODULE_KEY_VALUES = new Set<string>(['essentials-1']);
export const SAFE_MODULE_COMPLETION_SOURCE_VALUES = new Set<string>([
  'app',
  'legacy_1_0',
  'staff_recorded',
]);
/** A reset only ever ends a cycle that holds something, so never `not_started`. */
export const SAFE_PRIOR_MODULE_STATUS_VALUES = new Set<string>(['in_progress', 'complete']);

/**
 * Closed value set for the `field` tag: the columns of a migrated urine drug
 * test row (`legacy_test_results_urine`) that the reader parses and may find
 * outside the Galapagos vocabulary. A mirror of the parser's failure fields in
 * `apps/api/src/urine-test-results/repo/legacy-urine-history.ts`, whose test
 * asserts the two are equal; a new use of the tag extends this set.
 */
export const SAFE_FIELD_VALUES = new Set<string>(['screen_for', 'result', 'test_date']);

/** Closed `member_detail_changes.field` enum — detail-change publish-failure breakdown. */
export const SAFE_MEMBER_DETAIL_CHANGE_FIELD_VALUES = new Set<string>([
  'call_phone',
  'secondary_email',
  'home_address',
  'shipping_address',
  'emergency_contact',
  'emergency_clinic',
  'parental_consent',
  'programme',
]);

/** Closed `member_detail_change.request_source` enum for the publish-failure counter. */
export const SAFE_MEMBER_DETAIL_CHANGE_REQUEST_SOURCE_VALUES = new Set<string>([
  'call',
  'care_chat',
  'email',
  'staff_correction',
]);

/**
 * Closed value set for the `dispatch_reason` tag — a mirror of
 * `DISPATCH_TERMINAL_REASONS` (`packages/shared/src/notifications/schema.ts`).
 * The key is only bounded because its values come from that const, so an
 * off-vocabulary value must not ride the allowlisted key past the cardinality
 * boundary. This package sits below `@galapagos/shared` in the dependency graph
 * (shared consumes the provider interfaces defined here), so the const cannot
 * be imported; the metric-tags test drift-guards this mirror against the
 * schema source instead — the same arrangement as SAFE_SCREEN_ID_VALUES.
 */
export const SAFE_LICENCE_SOURCE_VALUES = new Set<string>([
  'backend2',
  'store',
  'healthie',
  'none',
]);

export const SAFE_AUTHOR_IDENTITY_VALUES = new Set<string>(['resolved', 'unresolved']);

export const SAFE_COHORT_VALUES = new Set<string>(['internal', 'member', 'unknown']);

export const SAFE_MEMBER_ORIGIN_VALUES = new Set<string>(['migrated', 'new', 'unknown']);

export const SAFE_AI_AVAILABILITY_STATUS_VALUES = new Set<string>([
  'available',
  'opted_out',
  'restricted',
]);

export const SAFE_AI_RESTRICTED_REASON_VALUES = new Set<string>(['state', 'oud', 'none']);

export const SAFE_PRIOR_ACTIVITY_VALUES = new Set<string>(['active', 'returning', 'no_history']);

export const SAFE_CHECKIN_CHANGE_WINDOW_VALUES = new Set<string>(['7d', '28d']);

export const SAFE_CHECKIN_CHANGE_DIRECTION_VALUES = new Set<string>(['up', 'same', 'down']);

/**
 * Closed value set for the `copy_guard_rule` tag — a mirror of
 * `COPY_GUARD_RULE` (`packages/shared/src/content-guard/outcome.ts`) plus the
 * push screen's own `blank` and `too_long`, which a push producer reports
 * under the same tag, and the plan-theme format's own rules
 * (`PLAN_THEME_GUARD_RULES` in
 * `packages/personalisation/src/formats/plan-theme/guard.ts`), which the weekly
 * plan reports under the same tag. This package sits below both, so the
 * consts cannot be imported; the metric-tags test drift-guards the mirror
 * against both sources.
 */
export const SAFE_COPY_GUARD_RULE_VALUES = new Set<string>([
  'clinical_label',
  'lockscreen_term',
  'contact_detail',
  'sona_title',
  'blank',
  'too_long',
  'numeral',
  'quit_language',
]);

/** The registered personalisation content kinds; a new kind adds its key here. */
export const SAFE_CONTENT_KIND_VALUES = new Set<string>([
  'care-message',
  'commitment-follow-up',
  'conversation-summary',
  'reminder-nudge',
  'weekly-plan-themes',
]);
export const SAFE_CONTENT_FORMAT_VALUES = new Set<string>([
  'push',
  'message',
  'plan-theme',
  'summary',
]);
export const SAFE_GUARD_CANDIDATE_VALUES = new Set<string>(['answer', 'fallback']);
/**
 * The push and plan-theme formats' guard rules plus `none` for a pass or a
 * message record. The push screen reports the shared copy screen's rules
 * between `blank` and `too_long`, so this set holds every `COPY_GUARD_RULE`
 * value as well.
 */
export const SAFE_GUARD_RULE_VALUES = new Set<string>([
  'blank',
  'lockscreen_term',
  'sona_title',
  'contact_detail',
  'too_long',
  'numeral',
  'quit_language',
  'clinical_label',
  'none',
]);

export const SAFE_DISPATCH_REASON_VALUES = new Set<string>([
  'operator_disabled',
  'unknown_definition',
  'expired',
  'no_content',
  'decision_missing',
  'no_email',
  'no_device',
  'provider_failed',
  'preference_opted_out',
  'voip_only',
]);

export const SAFE_ROW_KIND_VALUES = new Set<string>([
  'expo',
  'expo_with_voip',
  'voip_only',
  'malformed',
]);

/**
 * Closed value set for the `selection` tag on
 * `galapagos.care_assignment.default_assignment` — a mirror of
 * `DefaultPoolSelection` (`apps/api/src/care-assignment/service/assignment-types.ts`).
 */
export const SAFE_DEFAULT_POOL_SELECTION_VALUES = new Set<string>([
  'rules_matched',
  'unrestricted',
  'fallback',
]);

/**
 * Closed value set for the `telemedicine_completion_path` tag on
 * `galapagos.telemedicine.appointment_completed` — mirror of
 * `CareAppointmentCompletionPath` (`apps/api/src/care-scheduling/types.ts`).
 */
export const SAFE_TELEMEDICINE_COMPLETION_PATH_VALUES = new Set<string>([
  'call_ended',
  'window_lapsed',
  'attendance_recorded',
  'meeting_ended',
  'sweep',
]);

/**
 * Closed value set for the `care_section_state` tag — a mirror of
 * `CARE_SECTION_STATES` (`apps/api/src/home-surface/service/care-section-state.ts`).
 * An app cannot be imported from this package, so the metric-tags test
 * drift-guards the mirror against that source instead.
 */
export const SAFE_CARE_SECTION_STATE_VALUES = new Set<string>([
  'care_call_message',
  'care_call',
  'care_team_no_call',
  'upcoming_call_message',
  'upcoming_call',
  'specialist_thread',
  'introduction',
]);

/**
 * Closed value set for the `screen_id` tag — a mirror of the canonical
 * ScreenId registry (`packages/shared/src/screens/registry.ts`). The key is
 * only bounded because its values come from that registry, so an off-registry
 * or identity-bearing value must not ride the allowlisted key past the
 * cardinality boundary. This package sits below `@galapagos/shared` in the
 * dependency graph (shared consumes the provider interfaces defined here), so
 * the registry cannot be imported; the metric-tags test drift-guards this
 * mirror against the registry source instead.
 */
export const SAFE_SCREEN_ID_VALUES = new Set<string>([
  'home',
  'updates/required',
  'chat',
  'chat-choice',
  'app-setup',
  'registered-summary',
  'guided-conversations-intro',
  'guided-conversations-select',
  'profile',
  'profile-showcase',
  'care-specialist',
  'care-team-chat',
  'care-team-introduction',
  'care-first-call-booking',
  'hdhp-notice',
  'care-hdhp-call-booking',
  'care-call-matched',
  'care-upcoming-calls',
  'care-call-details',
  'care-previous-calls',
  'medication',
  'medication-cravings',
  'medication-cravings-product',
  'greeting',
  'login',
  'download',
  'signup',
  'signup/context',
  'signup/substance',
  'signup/legal-name',
  'signup/account',
  'signup/code',
  'signup/plan-details',
  'signup/eligibility-check',
  'signup/employer-search',
  'signup/address',
  'signup/birth-date',
  'signup/contact',
  'signup/quiz-motivation',
  'signup/quiz-goal',
  'signup/baseline-week',
  'signup/baseline-typical',
  'signup/baseline-adjust',
  'signup/screening',
  'signup/wrap-up',
  'signup/hdhp-notice',
  'signup/hdhp-booking',
  'signup/rewards-intro',
  'signup/rewards-points',
  'signup/call-choice',
  'signup/language-preference',
  'signup/booking',
  'signup/booking-unavailable',
  'signup/download',
  'signup/under-age',
  'signup/minor-unsupported',
  'signup/registered',
  'signup/error',
  'signup/book-a-call',
  'signup/login',
  'signup/login-code',
  'signup/login-no-progress',
  'signup/sso-contact',
  'signup/sso-code',
  'signup/terms-of-use',
  'signup/privacy-policy',
  'signup/notice-of-privacy-practices',
  'signup/consumer-health-data-privacy-notice',
  'signup/ai-notice',
  'signup/tcpa-authorization',
  'onboarding-start',
  'onboarding-name',
  'onboarding-motivation',
  'onboarding-substance',
  'onboarding-condition',
  'onboarding-goal',
  'onboarding-change-history',
  'onboarding-cutback-degree',
  'onboarding-sona-intro',
  'onboarding-weekly-usage',
  'onboarding-week-summary',
  'onboarding-typical-usage',
  'onboarding-taps2',
  'onboarding-summary',
  'onboarding-rewards-intro',
  'onboarding-conversation-mode',
  'intake-agent',
  'post-intake-feedback',
  'post-session-feedback',
  'quit-date-picker',
  'cbt-module',
  'intake-plan',
  'initial-review-intro',
  'initial-review-activity',
  'initial-review-check-ins',
  'eligibility-confirm',
  'eligibility-employer-search',
  'eligibility-benefit-holder',
  'eligibility-name',
  'eligibility-contact',
  'eligibility-address',
  'eligibility-sex-at-birth',
  'eligibility-no-match',
  'component-gallery',
  'consent-pending-hold',
  'debug-network',
  'eligibility-under-age',
  'eligibility-minor-blocked',
  'saml',
  'saml-contact',
  'assessment-showcase',
  'assessment-start',
  'assessment-phq2-q1',
  'assessment-phq2-q2',
  'assessment-gad2-q1',
  'assessment-gad2-q2',
  'assessment-phq9-q3',
  'assessment-phq9-q4',
  'assessment-phq9-q5',
  'assessment-phq9-q6',
  'assessment-phq9-q7',
  'assessment-phq9-q8',
  'assessment-phq9-q9',
  'assessment-gad7-q3',
  'assessment-gad7-q4',
  'assessment-gad7-q5',
  'assessment-gad7-q6',
  'assessment-gad7-q7',
  'assessment-session-suicide-screen',
  'assessment-complete',
  'assessment-result-no-mild',
  'assessment-result-moderate',
  'assessment-result-severe',
  'assessment-result-suicide-risk',
  'rewards',
  'rewards-history',
  'points-history',
  'redemption-history',
  'gift-cards',
  'gift-card-redeem',
  'gift-card-confirm',
  'gift-card-success',
  'gift-items',
  'gift-item-detail',
  'gift-item-confirm',
  'settings',
  'settings-notifications',
  'settings-my-data',
  'settings-my-data-privacy',
  'settings-legal',
  'settings-legal-privacy',
  'settings-legal-terms',
  'settings-legal-consumer-health',
  'settings-legal-privacy-practices',
  'weekly-plan-suggestions',
  'dev-developer-options',
  'dev-analytics-evidence',
  'dev-member-details',
  'dev-screen-preview',
  'dev-flag-override',
  'showcase-agent-launch',
  'telemedicine-call',
  'telemedicine-call-rating',
  'telemedicine-call-summary',
  'telemedicine-call-celebration',
  'cab-onboarding',
  'intake-ai-consent',
  'intake-withdrawal-check',
  'sona-state-notice',
  'sona-transfer',
  'migration-handoff',
  'migration-registration-consent',
  'migration-welcome',
  'migration-left-off',
  'migration-meet-sona',
  'migration-voice',
  'migration-ai-consent',
  'migration-preparing',
  'migration-gap-program',
  'migration-gap-goal',
  'migration-gap-motivation',
  'migration-text-preview',
  'cab-context-confirm',
  'call-staffing',
  'auth-page',
  'members-list',
  'today-view',
  'member-detail-changes',
  'member-details',
  'escalation-link',
  'care-outreach-queue',
  'eligibility-review',
  'escalation-review',
  'member-lookup',
  'scheduler-links',
  'telemedicine-appointments',
  'clinicians-list',
  'clinician-details',
  'staff-profile',
  'bam-home',
  'bam-questionnaire',
  'not-found',
]);

export function isSafeMetricTag(key: string, value?: string): boolean {
  if (key === 'context_key' && value !== undefined && value !== 'recent_pushes') return false;
  if (DENIED_METRIC_TAG_KEYS.has(key)) return false;
  if (!SAFE_METRIC_TAGS.has(key)) return false;
  if (key === 'route' && value !== undefined && looksInterpolated(value)) return false;
  if (key === 'source' && value !== undefined && !SAFE_SOURCE_VALUES.has(value)) return false;
  if (key === 'estate' && value !== undefined && !SAFE_ESTATE_VALUES.has(value)) return false;
  if (key === 'basis' && value !== undefined && !SAFE_BASIS_VALUES.has(value)) return false;
  if (key === 'token_source' && value !== undefined && !SAFE_TOKEN_SOURCE_VALUES.has(value))
    return false;
  if (key === 'consent_flow' && value !== undefined && !SAFE_CONSENT_FLOW_VALUES.has(value))
    return false;
  if (key === 'welcome_series' && value !== undefined && !SAFE_WELCOME_SERIES_VALUES.has(value))
    return false;
  if (key === 'veto_class' && value !== undefined && !SAFE_VETO_CLASS_VALUES.has(value))
    return false;
  if (key === 'planner_action' && value !== undefined && !SAFE_PLANNER_ACTION_VALUES.has(value))
    return false;
  if (key === 'journey_run_mode' && value !== undefined && !SAFE_JOURNEY_RUN_MODE_VALUES.has(value))
    return false;
  if (key === 'notice_variant' && value !== undefined && !SAFE_NOTICE_VARIANT_VALUES.has(value))
    return false;
  if (key === 'sona_intent_kind' && value !== undefined && !SAFE_SONA_INTENT_KIND_VALUES.has(value))
    return false;
  if (
    key === 'sona_launch_entry' &&
    value !== undefined &&
    !SAFE_SONA_LAUNCH_ENTRY_VALUES.has(value)
  )
    return false;
  if (
    key === 'sona_route_hold_outcome' &&
    value !== undefined &&
    !SAFE_SONA_ROUTE_HOLD_OUTCOME_VALUES.has(value)
  )
    return false;
  if (
    key === 'escalation_claim_action' &&
    value !== undefined &&
    !SAFE_ESCALATION_CLAIM_ACTION_VALUES.has(value)
  )
    return false;
  if (
    key === 'escalation_disposition' &&
    value !== undefined &&
    !SAFE_ESCALATION_DISPOSITION_VALUES.has(value)
  )
    return false;
  if (
    key === 'sona_admission_outcome' &&
    value !== undefined &&
    !SAFE_SONA_ADMISSION_OUTCOME_VALUES.has(value)
  )
    return false;
  if (key === 'tool' && value !== undefined && !SAFE_VOICE_TOOL_VALUES.has(value)) return false;
  if (key === 'outcome_id' && value !== undefined && !SAFE_OUTCOME_ID_VALUE.test(value))
    return false;
  if (key === 'screen_id' && value !== undefined && !SAFE_SCREEN_ID_VALUES.has(value)) return false;
  if (key === 'source_plan_kind' && value !== undefined && !SAFE_SOURCE_PLAN_KIND_VALUES.has(value))
    return false;
  if (key === 'landing_outcome' && value !== undefined && !SAFE_LANDING_OUTCOME_VALUES.has(value))
    return false;
  if (key === 'snapshot_source' && value !== undefined && !SAFE_SNAPSHOT_SOURCE_VALUES.has(value))
    return false;
  if (key === 'path' && value !== undefined && !SAFE_IMPORTED_RESOLUTION_PATH_VALUES.has(value))
    return false;
  if (key === 'product_kind' && value !== undefined && !SAFE_PRODUCT_KIND_VALUES.has(value))
    return false;
  if (key === 'field' && value !== undefined && !SAFE_FIELD_VALUES.has(value)) {
    if (!SAFE_MEMBER_DETAIL_CHANGE_FIELD_VALUES.has(value)) return false;
  }
  if (
    key === 'request_source' &&
    value !== undefined &&
    !SAFE_MEMBER_DETAIL_CHANGE_REQUEST_SOURCE_VALUES.has(value)
  )
    return false;
  if (key === 'ch_date_field' && value !== undefined && !SAFE_CH_DATE_FIELD_VALUES.has(value))
    return false;
  if (key === 'pharmacy_vendor' && value !== undefined && !SAFE_PHARMACY_VENDOR_VALUES.has(value))
    return false;
  if (
    key === 'pharmacy_order_outcome' &&
    value !== undefined &&
    !SAFE_PHARMACY_ORDER_OUTCOME_VALUES.has(value)
  )
    return false;
  if (
    key === 'pharmacy_vendor_step' &&
    value !== undefined &&
    !SAFE_PHARMACY_VENDOR_STEP_VALUES.has(value)
  )
    return false;
  if (
    key === 'pharmacy_order_status' &&
    value !== undefined &&
    !SAFE_PHARMACY_ORDER_STATUS_VALUES.has(value)
  )
    return false;
  if (
    key === 'pharmacy_order_age' &&
    value !== undefined &&
    !SAFE_PHARMACY_ORDER_AGE_VALUES.has(value)
  )
    return false;
  if (key === 'quit_date_source' && value !== undefined && !SAFE_QUIT_DATE_SOURCE_VALUES.has(value))
    return false;
  if (
    key === 'medication_source' &&
    value !== undefined &&
    !SAFE_MEDICATION_SOURCE_VALUES.has(value)
  )
    return false;
  if (key === 'module_key' && value !== undefined && !SAFE_MODULE_KEY_VALUES.has(value))
    return false;
  if (
    key === 'module_completion_source' &&
    value !== undefined &&
    !SAFE_MODULE_COMPLETION_SOURCE_VALUES.has(value)
  )
    return false;
  if (
    key === 'prior_module_status' &&
    value !== undefined &&
    !SAFE_PRIOR_MODULE_STATUS_VALUES.has(value)
  )
    return false;
  if (key === 'document_kind' && value !== undefined && !SAFE_DOCUMENT_KIND_VALUES.has(value))
    return false;
  if (key === 'dispatch_reason' && value !== undefined && !SAFE_DISPATCH_REASON_VALUES.has(value))
    return false;
  if (key === 'row_kind' && value !== undefined && !SAFE_ROW_KIND_VALUES.has(value)) return false;
  if (key === 'selection' && value !== undefined && !SAFE_DEFAULT_POOL_SELECTION_VALUES.has(value))
    return false;
  if (
    key === 'telemedicine_completion_path' &&
    value !== undefined &&
    !SAFE_TELEMEDICINE_COMPLETION_PATH_VALUES.has(value)
  )
    return false;
  if (key === 'author_identity' && value !== undefined && !SAFE_AUTHOR_IDENTITY_VALUES.has(value))
    return false;
  if (key === 'cohort' && value !== undefined && !SAFE_COHORT_VALUES.has(value)) return false;
  if (key === 'member_origin' && value !== undefined && !SAFE_MEMBER_ORIGIN_VALUES.has(value))
    return false;
  if (
    key === 'ai_availability_status' &&
    value !== undefined &&
    !SAFE_AI_AVAILABILITY_STATUS_VALUES.has(value)
  )
    return false;
  if (
    key === 'ai_restricted_reason' &&
    value !== undefined &&
    !SAFE_AI_RESTRICTED_REASON_VALUES.has(value)
  )
    return false;
  if (key === 'prior_activity' && value !== undefined && !SAFE_PRIOR_ACTIVITY_VALUES.has(value))
    return false;
  if (key === 'window' && value !== undefined && !SAFE_CHECKIN_CHANGE_WINDOW_VALUES.has(value))
    return false;
  if (
    key === 'direction' &&
    value !== undefined &&
    !SAFE_CHECKIN_CHANGE_DIRECTION_VALUES.has(value)
  )
    return false;
  if (key === 'licence_source' && value !== undefined && !SAFE_LICENCE_SOURCE_VALUES.has(value))
    return false;
  if (key === 'copy_guard_rule' && value !== undefined && !SAFE_COPY_GUARD_RULE_VALUES.has(value))
    return false;
  if (
    key === 'care_section_state' &&
    value !== undefined &&
    !SAFE_CARE_SECTION_STATE_VALUES.has(value)
  )
    return false;
  if (
    key === 'voice_timestamp_origin' &&
    value !== undefined &&
    !SAFE_VOICE_TIMESTAMP_ORIGIN_VALUES.has(value)
  )
    return false;
  if (key === 'origin' && value !== undefined && !SAFE_MEDICAL_ORDER_READ_ORIGIN_VALUES.has(value))
    return false;
  if (key === 'diagnosis_kind' && value !== undefined && !SAFE_DIAGNOSIS_KIND_VALUES.has(value))
    return false;
  if (
    key === 'healthie_reconcile_stream' &&
    value !== undefined &&
    !SAFE_HEALTHIE_RECONCILE_STREAM_VALUES.has(value)
  )
    return false;
  if (key === 'content_kind' && value !== undefined && !SAFE_CONTENT_KIND_VALUES.has(value))
    return false;
  if (key === 'content_format' && value !== undefined && !SAFE_CONTENT_FORMAT_VALUES.has(value))
    return false;
  if (key === 'guard_candidate' && value !== undefined && !SAFE_GUARD_CANDIDATE_VALUES.has(value))
    return false;
  if (key === 'guard_rule' && value !== undefined && !SAFE_GUARD_RULE_VALUES.has(value))
    return false;
  if (key === 'clinical_scope' && value !== undefined && !SAFE_CLINICAL_SCOPE_VALUES.has(value))
    return false;
  if (
    key === 'conversation_count_bucket' &&
    value !== undefined &&
    !SAFE_CONVERSATION_COUNT_BUCKET_VALUES.has(value)
  )
    return false;
  return true;
}

/**
 * Split metric tags into the allowlisted-safe subset and the denied keys. The
 * provider drops `denied` (loudly) rather than sending a cardinality bomb.
 */
export function filterMetricTags(tags: Record<string, string>): {
  safe: Record<string, string>;
  denied: string[];
} {
  const safe: Record<string, string> = {};
  const denied: string[] = [];
  for (const [key, value] of Object.entries(tags)) {
    if (isSafeMetricTag(key, value)) {
      safe[key] = value;
    } else {
      denied.push(key);
    }
  }
  return { safe, denied };
}

// Warn once per distinct denied key so a high-cardinality / identity tag is
// dropped *loudly* (ADR-069 cardinality guard) without per-call log spam.
const _warnedDeniedTags = new Set<string>();

/**
 * Allowlist metric tags for the client (browser / RN) RUM providers: keep only
 * the safe subset, drop denied keys (`usr.id`, `member_id`, session ids,
 * interpolated routes, …) so they never egress as RUM action attributes — the
 * same ADR-069 guard the node provider applies to its distribution metrics.
 */
export function safeMetricTags(tags?: Record<string, string>): Record<string, string> {
  const { safe, denied } = filterMetricTags(tags ?? {});
  const fresh = denied.filter((k) => !_warnedDeniedTags.has(k));
  if (fresh.length > 0) {
    for (const k of fresh) _warnedDeniedTags.add(k);
    console.warn(
      `[observability] dropped high-cardinality metric tag(s) not on the allowlist (ADR-069): ${fresh.join(', ')}`,
    );
  }
  return safe;
}
